import { ImprList, Modal } from '@imprivata-cloud/components';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConsentGrantType, FactorType } from '../../api/constants';
import { revokeConsent } from '../../api/selfServiceServices';
import type { SelfServiceLandingPageDataResponse } from '../../api/types';
import AuthenticatorNameAndIcon from '../../components/authenticatorNameAndIcon/AuthenticatorNameAndIcon';
import { AppError } from '../../errorHandler/errors';
import { useSession } from '../../common/authorization/context/Session';
import {
  landingPageDataTanstackCacheKey,
  useUnenrollAuthenticator,
} from '../../portal/containers/users/hooks/useAuthenticators';
import { useErrorContext } from '../../errorHandler/context/ErrorContext';
import { useSelfServiceData } from '../utils/useSelfServiceData';
import { useNavigate } from 'react-router';
import { SelfServicePath } from '../SelfServiceRoutes';
import AuthenticatorActionItems from '../../components/authenticatorActionItems/AuthenticatorActionItems';
import FaceIcon from '../../assets/icons/authenticators/face_bio.svg?react';
import PasswordIcon from '../../assets/icons/authenticators/password.svg?react';
import Icon from '@ant-design/icons';
import AuthMethodListHeader, { type AuthenticatorUiData } from './AuthMetodListHeader/AuthMethodListHeader';
import QuestionImg from '../../assets/icons/question.svg?react';
import EmptyImage from '../../assets/self-service-authenticator-list-empty-table.svg?react';
import getRelativeTime from '../../common/authMethodInfo/relativeTime';
import './AuthenticatorList.less';
import { hasCamera } from '../utils/helpers';

const columns: ColumnType[] = [
  {
    title: 'Name',
    dataIndex: 'name-320',
    key: 'name-320',
    responsive: ['xs'],
    render: (_, record) => (
      <>
        <AuthenticatorNameAndIcon />
        <span style={{ color: 'var(--grey-50)', paddingTop: '9px' }}>{record.lastUsed}</span>
      </>
    ),
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    responsive: ['sm'],
    render: () => <AuthenticatorNameAndIcon />,
  },
  {
    title: 'Last Used',
    dataIndex: 'lastUsed',
    key: 'lastUsed',
    responsive: ['sm'],
    render: (_, record) => <span style={{ color: 'var(--grey-50)' }}>{record.lastUsed}</span>,
  },
  {
    dataIndex: 'action',
    key: 'action',
    render: (_, record) => (
      <AuthenticatorActionItems
        key={record.key}
        factorType={record.factorType}
        onRemove={record.onRemove}
        onRevokeConsent={record?.onRevokeConsent}
      />
    ),
  },
];

const AuthenticatorList = () => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { tenantId, userInfo } = useSession();
  const { mutate, isPending: isLoadingUnenrollAuthenticator } = useUnenrollAuthenticator(tenantId ?? '', {
    onSettled: () => setAuthenticatorToRemove(undefined),
    onError: () => emitError(AppError.fromMessage('Error removing authenticator')),
  });

  const [authenticatorToRemove, setAuthenticatorToRemove] = useState<string>();
  const [consentRevokeDialog, setConsentRevokeDialog] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  const {
    data: selfServiceLandingPageData,
    isError: isErrorSelfServiceLandingPageData,
    isLoading: isLoadingSelfServiceLandingPageData,
  } = useSelfServiceData();

  const authenticatorData: Record<string, AuthenticatorUiData> = {
    face: {
      text: t('self-service.auth-method.face'),
      icon: <Icon component={FaceIcon} />,
      onClick: () => navigate(SelfServicePath.SELF_SERVICE_ENROLL_FACE.slice(1)),
    },
    password: {
      text: t('self-service.auth-method.password.authn-password'),
      icon: <Icon component={PasswordIcon} />,
      onClick: () => navigate(SelfServicePath.SELF_SERVICE_IDENTITY_PASSWORD.slice(1)),
    },
  };

  const [availableAuthMethods, setAvailableAuthMethods] = useState<Record<string, AuthenticatorUiData>>({});

  if (isErrorSelfServiceLandingPageData) {
    emitError(AppError.fromMessage('Error fetching authenticators'));
  }

  // consentType was removed on API V2. Is is guaranteed to return only the impr.biometrics consent type here.
  const consentInfo = selfServiceLandingPageData?.consentInfos?.[0];

  const authData = selfServiceLandingPageData?.authenticators
    ?.filter((a) => a.factorType === FactorType.Face) // Currently we only support face authenticator, this will change when we add more authenticators to USS
    .map((auth) => ({
      key: auth.authenticatorId,
      factorType: auth.factorType,
      lastUsed: getRelativeTime(auth.lastUsedAt),
      onRemove: () => setAuthenticatorToRemove(auth.authenticatorId),
      onRevokeConsent:
        consentInfo?.grantType === ConsentGrantType.GRANTED ? () => setConsentRevokeDialog(true) : undefined,
    }));

  useEffect(() => {
    if (!isLoadingSelfServiceLandingPageData && selfServiceLandingPageData) {
      addFaceMethodToAvailableAuthenticators(selfServiceLandingPageData);
    }
  }, [selfServiceLandingPageData, isLoadingSelfServiceLandingPageData]);

  const onRemove = () => {
    if (authenticatorToRemove === undefined) {
      return; // To satisfy ts
    }
    mutate(authenticatorToRemove, {
      onSuccess: () => {
        // remove the authenticator from the React-Query cache list
        queryClient.setQueryData<SelfServiceLandingPageDataResponse>([landingPageDataTanstackCacheKey], (oldData) => {
          return {
            ...oldData,
            authenticators: oldData?.authenticators?.filter((auth) => auth.authenticatorId !== authenticatorToRemove),
          } as SelfServiceLandingPageDataResponse;
        });
      },
    });
  };

  const onRevokeConsent = () => {
    if (!consentRevokeDialog) {
      return; // To satisfy ts
    }
    setDialogLoading(true);
    revokeConsent({
      consentRevokeList:
        consentInfo?.documentMetadataList.filter((d) => d.documentPurpose !== 'biometric-notice') || [],
    })
      .then(() => {
        // revoking consent unenrolls the authenticator
        queryClient.invalidateQueries({
          queryKey: [landingPageDataTanstackCacheKey],
          refetchType: 'all',
        });
      })
      .catch(() => {
        emitError(AppError.fromMessage('Error revoking consent'));
      })
      .finally(() => {
        setDialogLoading(false);
        setConsentRevokeDialog(false);
      });
  };

  const addFaceMethodToAvailableAuthenticators = async (
    selfServiceLandingPageData: SelfServiceLandingPageDataResponse,
  ) => {
    const activeFaceMethod = selfServiceLandingPageData?.authenticators.find((el) => el.factorType === FactorType.Face);
    const hasImprivataIdentity = selfServiceLandingPageData?.userInfo.userId;

    const newMethods: Record<string, AuthenticatorUiData> = {};

    const IMPRIVATA_DOMAIN = 'imprivata.id';
    const hasImprivataId =
      userInfo?.email?.includes(IMPRIVATA_DOMAIN) || userInfo?.userDisplayName?.includes(IMPRIVATA_DOMAIN);

    if (hasImprivataId) {
      newMethods.password = authenticatorData.password;
    }

    if (!activeFaceMethod && hasImprivataIdentity && (await hasCamera())) {
      newMethods.face = authenticatorData.face;
    }

    setAvailableAuthMethods(newMethods);
  };

  return (
    <div className='authenticator-list-container'>
      <Modal
        open={!!authenticatorToRemove}
        onCancel={() => setAuthenticatorToRemove(undefined)}
        cancelButtonProps={{ type: 'primary' }}
        okButtonProps={{ type: 'default', loading: isLoadingUnenrollAuthenticator }}
        okText={t('self-service.authenticator-list.confirm-remove-face-btn')}
        cancelText={t('self-service.authenticator-list.cancel-remove-face')}
        onOk={onRemove}
        onClose={() => setAuthenticatorToRemove(undefined)}
        centered={true}
        closeIcon={false}
        className="self-service-confirmation-modal"
      >
        <QuestionImg className="modal-graphic" />
        <h2>{t('self-service.authenticator-list.remove-authenticator-title')}</h2>
        <p>{t('self-service.authenticator-list.confirm-remove-face')}</p>
      </Modal>

      <Modal
        open={!!consentRevokeDialog}
        onCancel={() => setConsentRevokeDialog(false)}
        cancelButtonProps={{ type: 'primary' }}
        okButtonProps={{ type: 'default', loading: dialogLoading }}
        okText={t('self-service.authenticator-list.confirm-revoke-consent')}
        cancelText={t('self-service.authenticator-list.cancel-revoke-consent')}
        onOk={onRevokeConsent}
        onClose={() => setConsentRevokeDialog(false)}
        centered={true}
        closeIcon={false}
        className="self-service-confirmation-modal"
        autoFocusButton="cancel"
      >
        <QuestionImg className="modal-graphic" />
        <h2>{t('self-service.authenticator-list.revoke-consent-title')}</h2>
        <p>{t('self-service.authenticator-list.revoke-consent-content')}</p>
      </Modal>

      <ImprList
        loading={isLoadingSelfServiceLandingPageData}
        columns={columns}
        dataSource={authData as Record<string, unknown>[]}
        showHeader={false}
        pagination={false}
        enableSearch={false}
        headerExtraComponents={[
          <AuthMethodListHeader authenticatorsList={availableAuthMethods} key="table-extra-header" />,
        ]}
        locale={{
          emptyText: (
            <div className={'empty-list-container'}>
              <EmptyImage style={{ margin: '1rem' }} />
              <div className={'empty-list-container-title'}>{t('self-service.authenticator-list.empty-text')}</div>
            </div>
          ),
        }}
      />
    </div>
  );
};

export default AuthenticatorList;
